/**
 * Admin V3 — Onboarding module — Table component
 *
 * GOVERNANCE MEANING: Pending Driver Approvals View (read-only).
 *
 * 6 columns: Candidat (avatar + name + email) | Téléphone | Véhicule
 * (model + plate) | Statut (always "En attente" — endpoint is fixed-filter)
 * | Soumis le (created_at) | Actions (open icon → detail placeholder).
 *
 * Row click → /admin-v3/onboarding/:driverId (placeholder per Phase 2.4
 * decision; future phase replaces the DetailPlaceholder).
 *
 * NO mutation surface. NO approve/reject buttons (Phase 3+ scope per
 * governance). NO legacy /admin Beta tab handoff link (owner ruling).
 *
 * Visual reuse from visualTokens.js: SmallPill + driverApprovalMeta
 * (defined alongside Phase 2.3 additions). EmptyState + error banner
 * pattern shared with BookingsListTable / DriversListTable.
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ExternalLink, UserPlus, AlertCircle, RotateCcw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  cn,
  EmptyState,
  SmallPill,
  driverApprovalMeta,
} from '../../shared/visualTokens';
import { t } from '../../i18n';

const formatDate = (iso, locale) => {
  if (!iso) return '—';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'fr-FR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    }).format(d);
  } catch {
    return String(iso);
  }
};

const initialsOf = (name) =>
  (name || '')
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';

const SkeletonRows = () => (
  <>
    {Array(5).fill(0).map((_, i) => (
      <tr key={`sk-${i}`} className="border-b border-slate-100">
        {Array(6).fill(0).map((_, j) => (
          <td key={j} className="px-4 py-3.5">
            <div className="h-3 w-full max-w-[120px] rounded bg-slate-100 animate-pulse" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const OnboardingListTable = ({ candidates, loading, error, isFilterActive, onRetry, locale = 'fr' }) => {
  const navigate = useNavigate();

  const errorBanner = error ? (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 mb-3" data-testid="adminv3-onboarding-error">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-rose-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-rose-700">{t('onboarding.error.title', locale)}</p>
          <p className="text-xs text-rose-600 mt-1 break-words">{error}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRetry}
          className="rounded-lg border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
          data-testid="adminv3-onboarding-retry"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          {t('onboarding.error.retry', locale)}
        </Button>
      </div>
    </div>
  ) : null;

  const emptyContent = (
    <EmptyState
      icon={UserPlus}
      title={t('onboarding.empty.title', locale)}
      desc={t(isFilterActive ? 'onboarding.empty.descFilter' : 'onboarding.empty.descNoFilter', locale)}
    />
  );

  return (
    <>
      {errorBanner}
      <div className="overflow-x-auto rounded-2xl border border-slate-200">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {[
                t('onboarding.col.candidate', locale),
                t('onboarding.col.phone', locale),
                t('onboarding.col.vehicle', locale),
                t('onboarding.col.status', locale),
                t('onboarding.col.submitted', locale),
                t('onboarding.col.actions', locale),
              ].map((h, i) => (
                <th key={i} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : candidates.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8">{emptyContent}</td>
              </tr>
            ) : (
              candidates.map((c) => {
                // Endpoint is fixed-filter to "pending"; defensive fallback if drift.
                const approvalKey = c.approval_status || 'pending';
                const approvalMetaFor = driverApprovalMeta[approvalKey] || driverApprovalMeta.pending;
                // AV3 A-13: onboarding rows open the canonical driver detail
                // (review + approve/reject live there); the onboarding/:id
                // placeholder is removed.
                const detailPath = `/admin/drivers/${c.id}`;

                return (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors">
                    {/* Candidat (avatar + name + email) */}
                    <td className="px-4 py-3.5">
                      <Link
                        to={detailPath}
                        className="flex items-center gap-2.5 group"
                        data-testid={`adminv3-onboarding-row-${c.id}`}
                      >
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-[11px] font-bold text-amber-400 flex-shrink-0">
                          {initialsOf(c.name)}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 group-hover:text-amber-700 truncate">
                            {c.name || '—'}
                          </div>
                          <div className="text-[11px] text-slate-400 truncate">{c.email || ''}</div>
                        </div>
                      </Link>
                    </td>

                    {/* Téléphone */}
                    <td className="px-4 py-3.5 text-slate-700 font-mono text-[12px]">
                      {c.phone || '—'}
                    </td>

                    {/* Véhicule */}
                    <td className="px-4 py-3.5">
                      <div className="text-slate-700">{c.vehicle_model || '—'}</div>
                      <div className="text-[11px] text-slate-400 font-mono">{c.license_plate || ''}</div>
                    </td>

                    {/* Statut */}
                    <td className="px-4 py-3.5">
                      <SmallPill meta={approvalMetaFor} />
                    </td>

                    {/* Soumis le */}
                    <td className="px-4 py-3.5 text-slate-500 text-[12px]">
                      {formatDate(c.created_at, locale)}
                    </td>

                    {/* Actions (open detail placeholder only — NO approve/reject) */}
                    <td className="px-4 py-3.5">
                      <button
                        type="button"
                        onClick={() => navigate(detailPath)}
                        className={cn(
                          'rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors'
                        )}
                        title={t('onboarding.action.open', locale)}
                        data-testid={`adminv3-onboarding-open-${c.id}`}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default OnboardingListTable;
